import { useCallback, useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import CancelMembershipDialog from "@/components/CancelMembershipDialog";

export const SETTINGS_IDS = {
  container: "settings-container",
  nameInput: "settings-name-input",
  saveNameBtn: "settings-save-name-button",
  manageMembershipBtn: "settings-manage-membership-button",
  openWalletBtn: "settings-open-wallet-button",
  cancelMembershipBtn: "settings-cancel-membership-button",
  depositStatus: "settings-deposit-status",
  updateBankingBtn: "settings-update-banking-button",
  autoWithdrawToggle: "settings-auto-withdraw-toggle",
  autoWithdrawMin: "settings-auto-withdraw-min",
  autoWithdrawSave: "settings-auto-withdraw-save",
};

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [bankingBusy, setBankingBusy] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMin, setAutoMin] = useState("5.00");
  const [autoBusy, setAutoBusy] = useState(false);

  const loadWallet = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet");
      setWalletData(data);
      if (data?.auto_withdraw) {
        setAutoEnabled(!!data.auto_withdraw.enabled);
        const min = data.auto_withdraw.min_cents ?? data.auto_withdraw.floor_cents ?? 500;
        setAutoMin((min / 100).toFixed(2));
      }
    } catch (err) {
      console.warn("Wallet load failed", err);
    }
  }, []);

  useEffect(() => {
    if (user) loadWallet();
  }, [user, loadWallet]);

  useEffect(() => {
    if (params.get("connect") === "updated") {
      toast.success("Banking info updated");
      loadWallet();
    }
  }, [params, loadWallet]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0b0c10]">
        <Navbar />
        <div className="max-w-xl mx-auto p-10 text-slate-300 text-center">
          Please <Link to="/login" className="underline text-yellow-400">sign in</Link> to access settings.
        </div>
      </div>
    );
  }

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Name cannot be empty");
    setBusy(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const openStripeBanking = async () => {
    setBankingBusy(true);
    try {
      const endpoint = walletData?.connect?.account_id
        ? "/wallet/connect/update-link"
        : "/wallet/connect/onboard";
      const { data } = await api.post(endpoint, { return_origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stripe banking link failed");
      setBankingBusy(false);
    }
  };

  const saveAutoWithdraw = async (nextEnabled, nextMinCents) => {
    setAutoBusy(true);
    try {
      await api.post("/wallet/auto-withdraw", {
        enabled: nextEnabled,
        min_cents: nextMinCents,
      });
      toast.success(nextEnabled ? "Auto-withdraw on" : "Auto-withdraw off");
      await loadWallet();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setAutoBusy(false);
    }
  };

  const connect = walletData?.connect || {};
  const payoutsReady = !!connect.payouts_enabled;
  const floorCents = walletData?.auto_withdraw?.floor_cents ?? 500;

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain" data-testid={SETTINGS_IDS.container}>
      <Navbar />
      <CancelMembershipDialog open={cancelOpen} onOpenChange={setCancelOpen} />
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-10 space-y-6">
        <p className="telesis-eyebrow">Settings</p>
        <h1 className="text-3xl md:text-4xl font-black text-slate-100 tracking-tight">Preferences</h1>

        <section className="telesis-panel rounded p-6 space-y-4">
          <p className="telesis-eyebrow">Profile</p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Display name</Label>
            <Input
              data-testid={SETTINGS_IDS.nameInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
            <p className="mt-1 font-mono text-sm text-slate-300">{user.email}</p>
          </div>
          <Button
            data-testid={SETTINGS_IDS.saveNameBtn}
            onClick={saveName}
            disabled={busy || name.trim() === user.name}
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            {busy ? "Saving…" : "Save profile"}
          </Button>
        </section>

        <section className="telesis-panel rounded p-6 space-y-4">
          <p className="telesis-eyebrow">Direct deposit · banking info</p>
          <p data-testid={SETTINGS_IDS.depositStatus} className="text-sm text-slate-300">
            {connect.account_id ? (
              <>
                Stripe Connect account{" "}
                <span className="font-mono text-slate-100">{connect.account_id}</span>
                {" · "}
                <span className={payoutsReady ? "text-emerald-300" : "text-rose-300"}>
                  {payoutsReady ? "payouts enabled" : "payouts not enabled"}
                </span>
              </>
            ) : (
              <>No bank account linked yet. Add one to receive direct deposits for monthly payouts and cashouts.</>
            )}
          </p>
          <Button
            data-testid={SETTINGS_IDS.updateBankingBtn}
            onClick={openStripeBanking}
            disabled={bankingBusy}
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            {bankingBusy
              ? "Opening Stripe…"
              : connect.account_id
              ? "Update banking info"
              : "Link bank account"}
          </Button>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            Banking info is managed securely by Stripe. Telesis never stores your account details.
          </p>
        </section>

        <section className="telesis-panel rounded p-6 space-y-3">
          <p className="telesis-eyebrow">Monthly auto-payout</p>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-slate-200">Sweep balance to bank automatically</p>
              <p className="text-xs text-slate-500">Runs daily; triggers only when balance ≥ minimum.</p>
            </div>
            <Switch
              data-testid={SETTINGS_IDS.autoWithdrawToggle}
              checked={autoEnabled}
              disabled={autoBusy || !payoutsReady}
              onCheckedChange={(v) => {
                setAutoEnabled(v);
                const min = Math.max(floorCents, Math.round(parseFloat(autoMin || "0") * 100));
                saveAutoWithdraw(v, min);
              }}
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Minimum (CAD)</Label>
              <Input
                data-testid={SETTINGS_IDS.autoWithdrawMin}
                type="number"
                min={(floorCents / 100).toFixed(2)}
                step="0.5"
                value={autoMin}
                onChange={(e) => setAutoMin(e.target.value)}
                className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
                disabled={!payoutsReady}
              />
            </div>
            <Button
              data-testid={SETTINGS_IDS.autoWithdrawSave}
              onClick={() => {
                const min = Math.max(floorCents, Math.round(parseFloat(autoMin || "0") * 100));
                saveAutoWithdraw(autoEnabled, min);
              }}
              variant="outline"
              disabled={autoBusy || !payoutsReady}
              className="rounded-full border-[#2a2f3f] bg-transparent text-slate-100 hover:bg-[#1a1d28]"
            >
              Save minimum
            </Button>
          </div>
          {!payoutsReady && (
            <p className="text-[10px] font-mono text-rose-300 uppercase tracking-widest">
              Link your bank account above first.
            </p>
          )}
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            Floor: ${(floorCents / 100).toFixed(2)} CAD
          </p>
        </section>

        <section className="telesis-panel rounded p-6 space-y-4">
          <p className="telesis-eyebrow">Membership</p>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge
              className={`font-mono text-[10px] tracking-widest uppercase ${
                user.is_member
                  ? user.membership_tier === "unlimited"
                    ? "bg-yellow-400/15 text-yellow-300 border-yellow-500/40"
                    : "bg-emerald-500/15 text-emerald-300 border-emerald-700/40"
                  : "bg-slate-500/15 text-slate-300 border-slate-700/40"
              }`}
            >
              {user.is_member ? (user.membership_tier === "unlimited" ? "Unlimited · 55.69 CAD" : "Base · 20.21 CAD") : "Not a member"}
            </Badge>
            {user.is_member && user.membership_tier !== "unlimited" && user.votes_remaining_this_month !== null && (
              <span className="text-[10px] font-mono text-slate-400">
                {user.votes_remaining_this_month} votes left this month
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid={SETTINGS_IDS.manageMembershipBtn}
              onClick={() => navigate("/feed")}
              variant="outline"
              className="rounded-full border-[#2a2f3f] bg-transparent text-slate-100 hover:bg-[#1a1d28]"
            >
              {user.is_member ? "Change tier" : "Join · from 20.21 CAD"}
            </Button>
            {user.is_member && (
              <Button
                data-testid={SETTINGS_IDS.cancelMembershipBtn}
                onClick={() => setCancelOpen(true)}
                variant="outline"
                className="rounded-full border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10"
              >
                Cancel membership
              </Button>
            )}
          </div>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            Cancellation takes effect at the end of the current billing period.
          </p>
        </section>

        <section className="telesis-panel rounded p-6 space-y-4">
          <p className="telesis-eyebrow">Wallet &amp; payouts</p>
          <p className="text-sm text-slate-300">
            Add funds, request cashouts and view your ledger from the Wallet page.
          </p>
          <Button
            data-testid={SETTINGS_IDS.openWalletBtn}
            onClick={() => navigate("/wallet")}
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            Open wallet
          </Button>
        </section>

        <section className="telesis-panel rounded p-6 space-y-2">
          <p className="telesis-eyebrow">Age verification</p>
          <p className="text-sm text-slate-300">
            Status:{" "}
            <span className={user.age_verified ? "text-emerald-300" : "text-rose-300"}>
              {user.age_verified ? "verified" : "not verified"}
            </span>
          </p>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            Required before joining the membership and voting.
          </p>
        </section>
      </main>
    </div>
  );
}
